
import Vue from 'vue';
import Component from 'vue-class-component';
import {namespace} from 'vuex-class';
import {Watch} from 'vue-property-decorator';
import {IEligibleLocker, IEligibleService} from '@/store/modules/orderModule';
import {TranslationHelper} from '@/helpers/TranslationHelper';
import {UrlHelper} from '@/helpers/UrlHelper';
import {PermissionHelper} from '@/helpers/PermissionHelper';
import {EUserPermissions} from '@/model/EUserPermissions';

const orderModule = namespace('orderModule');
const userModule = namespace('userModule');

interface IOrderLine {
  service: IEligibleService;
  quantity: number;
}

interface IGenericArticleLine {
  description: string;
  price: number;
  serviceProviderId: number;
  providerName: string;
  lockerCellSizeId: number|null;
}

@Component({
  name: 'OrderCreate',
  filters: {
    currency(val: number) {
      if (val === null || val === undefined) return '—';
      return val.toFixed(2) + ' €';
    },
  },
})
export default class OrderCreate extends Vue {

  @orderModule.Getter('eligibleLockers') eligibleLockers!: IEligibleLocker[];
  @orderModule.Getter('canCreateOfficeOrders') canCreateOfficeOrders!: boolean;
  @orderModule.Getter('eligibleServices') eligibleServices!: IEligibleService[];
  @orderModule.Getter('lockerCellSizes') lockerCellSizes!: any[];
  @orderModule.Getter('eligibleServiceProviders') eligibleServiceProviders!: any[];
  @orderModule.Getter('order') createdOrder!: any;
  @userModule.Getter('addresses') userAddresses!: any[];

  protected step: number = 1;

  protected selectedUser: any = null;
  protected userSearch: string = '';
  protected userSearchResults: any[] = [];
  protected searchingUsers: boolean = false;
  protected userSearchTimer: any = null;

  // 'locker' (drop cell in a locker) or 'address' (office order delivered to one of the
  // customer's addresses, no locker involved) — mirrors old backoffice's create-with-customer step 2
  protected locationType: 'locker'|'address' = 'locker';
  protected selectedLockerId: number|null = null;
  protected loadingLockers: boolean = false;
  protected selectedAddressId: number|null = null;
  protected loadingAddresses: boolean = false;

  protected loadingServices: boolean = false;
  protected orderLines: IOrderLine[] = [];
  protected serviceToAdd: any = null;
  protected selectedServicesToAdd: any[] = [];
  protected promoCode: string = '';
  protected note: string = '';
  // 'pickup'|'confirm', only relevant/required for locationType === 'address' — mirrors old
  // backoffice's create-with-customer status radio (status_pickup_office/status_delivery_office)
  protected initialStatus: 'pickup'|'confirm'|null = null;

  protected creating: boolean = false;
  protected createError: string = '';
  protected subscribes: any = null;

  protected showGenericDialog: boolean = false;
  protected genericForm: {description: string; price: number; serviceProviderId: number|null; lockerCellSizeId: number|null} = {
    description: '',
    price: 0,
    serviceProviderId: null,
    lockerCellSizeId: null,
  };
  protected genericArticleLines: IGenericArticleLine[] = [];
  protected genericCellSizeError: string = '';
  protected genericPriceError: string = '';

  created() {
    this.subscribes = (this as any).$ons({
      'backend.call.OK.eligibleLockersLoaded': () => { this.loadingLockers = false; },
      'backend.call.OK.addressesLoaded': () => { this.loadingAddresses = false; },
      'backend.call.OK.eligibleServicesLoaded': () => { this.loadingServices = false; },
      'backend.call.OK.orderCreated': () => {
        this.creating = false;
        if (this.createdOrder && this.createdOrder.id) {
          this.$router.push({name: 'order', params: {orderId: String(this.createdOrder.id)}});
        }
      },
      'backend.call.KO': () => {
        this.creating = false;
        this.loadingLockers = false;
        this.loadingAddresses = false;
        this.loadingServices = false;
        this.createError = this.$t('common.error').toString();
      },
    });
  }

  beforeDestroy() {
    (this as any).$offs(this.subscribes);
  }

  @Watch('userSearch')
  onUserSearchChanged(val: string) {
    if (!val || val.length < 3) {
      clearTimeout(this.userSearchTimer);
      this.userSearchResults = [];
      this.searchingUsers = false;
      return;
    }
    clearTimeout(this.userSearchTimer);
    this.searchingUsers = true;
    this.userSearchTimer = setTimeout(() => {
      this.$store.dispatch('userModule/fetchUserSearch', val).then(() => {
        this.userSearchResults = this.$store.getters['userModule/userSearch'] || [];
        this.searchingUsers = false;
      }).catch(() => { this.searchingUsers = false; });
    }, 300);
  }

  @Watch('selectedUser')
  onSelectedUserChanged(newVal: any, oldVal: any) {
    // Switching to a different customer invalidates any locker/address already picked for the
    // previous one (an address especially — it belongs to a specific customer)
    if (!oldVal || !newVal || newVal.id !== oldVal.id) {
      this.selectedLockerId = null;
      this.selectedAddressId = null;
      this.initialStatus = null;
      this.orderLines = [];
    }
  }

  get lockerItems() {
    return this.eligibleLockers.map((l) => ({
      id: l.id,
      label: l.city ? l.name + ' (' + l.city + ')' : l.name,
    }));
  }

  get selectedLockerName() {
    const found = this.eligibleLockers.find((l) => l.id === this.selectedLockerId);
    if (!found) { return ''; }
    return found.city ? found.name + ' (' + found.city + ')' : found.name;
  }

  get addressItems() {
    return (this.userAddresses || []).map((a: any) => {
      const shortName = (a.name && String(a.name).trim()) ? a.name : a.line_1;
      return {
        id: a.id,
        label: a.city ? shortName + ' (' + a.city + ')' : shortName,
      };
    });
  }

  get selectedAddressLabel() {
    const found = this.addressItems.find((a: any) => a.id === this.selectedAddressId);
    return found ? found.label : '';
  }

  get selectedLocationLabel() {
    return this.locationType === 'address' ? this.selectedAddressLabel : this.selectedLockerName;
  }

  get canGoToStep3(): boolean {
    return this.locationType === 'address' ? !!this.selectedAddressId : !!this.selectedLockerId;
  }

  get availableServices() {
    return this.eligibleServices;
  }

  get selectableServices() {
    const addedIds = this.orderLines.map((l) => l.service.id);
    return this.eligibleServices
      .filter((s) => addedIds.indexOf(s.id) === -1)
      .map((s) => ({
        id: s.id,
        label: (s.mandatory ? '★ ' : '') + this.serviceDisplayName(s) + (s.service_provider ? ' — ' + s.service_provider.name : ''),
        service: s,
        mandatory: s.mandatory,
      }));
  }

  get mandatoryServicesList(): IEligibleService[] {
    return this.eligibleServices.filter((s) => s.mandatory);
  }

  get hasMandatoryService(): boolean {
    if (!this.mandatoryServicesList.length) { return true; }
    const addedIds = this.orderLines.map((l) => l.service.id);
    return this.mandatoryServicesList.some((s) => addedIds.indexOf(s.id) !== -1);
  }

  get canSubmit(): boolean {
    const hasLines = this.orderLines.length > 0 || this.genericArticleLines.length > 0;
    if (this.locationType === 'address') {
      return hasLines && !!this.initialStatus;
    }
    return hasLines;
  }

  get lockerCellSizesWithNull(): any[] {
    const noOption = { id: null, name: String(this.$t('orderCreate.step3.genericArticleNoCompartment')) };
    const sizes = (Array.isArray(this.lockerCellSizes) ? this.lockerCellSizes : []).map(function(s: any) {
      const n = s.name;
      return {
        id: s.id,
        name: (typeof n === 'object' && n !== null) ? (TranslationHelper.translateField(n) || String(n)) : String(n || ''),
      };
    });
    return [noOption].concat(sizes);
  }

  get eligibleProviders(): any[] {
    return Array.isArray(this.eligibleServiceProviders) ? this.eligibleServiceProviders : [];
  }

  get isAdmin(): boolean {
    const userData = this.$store.getters['authModule/userData'];
    return userData && PermissionHelper.hasPermissionAny(userData, EUserPermissions.PERMISSION_ADMINISTRATOR);
  }

  get oldBackofficeUrl(): string {
    return UrlHelper.mayorOrderCreate();
  }

  get selectedServiceProviderIds(): number[] {
    const ids: number[] = [];
    this.orderLines.forEach((l) => {
      // Skip professional SP virtual entries (negative id) — they don't have real Service records
      if (l.service.id !== null && l.service.id > 0 && ids.indexOf(l.service.service_provider_id) === -1) {
        ids.push(l.service.service_provider_id);
      }
    });
    return ids;
  }

  serviceDisplayName(service: IEligibleService): string {
    if (!service.name) { return '—'; }
    if (typeof service.name === 'object') {
      return TranslationHelper.translateField(service.name);
    }
    return service.name;
  }

  linePrice(line: IOrderLine): number {
    const s = line.service;
    const unitPrice = s.on_sale && s.sale_price ? s.sale_price : s.price;
    return unitPrice * line.quantity;
  }

  get orderTotal(): number {
    const regularTotal = this.orderLines.reduce((sum, l) => sum + this.linePrice(l), 0);
    const genericTotal = this.genericArticleLines.reduce((sum, g) => sum + g.price, 0);
    return regularTotal + genericTotal;
  }

  onStep1Next() {
    if (!this.selectedUser) { return; }
    this.step = 2;
    this.loadEligibleLockers();
    // preloaded so switching to the "customer address" tab (only shown once canCreateOfficeOrders
    // is known) doesn't need an extra round-trip, and so it refreshes if the customer changed
    this.loadingAddresses = true;
    this.$store.dispatch('userModule/fetchAddresses', this.selectedUser.id);
  }

  loadEligibleLockers() {
    this.loadingLockers = true;
    this.$store.dispatch('orderModule/fetchEligibleLockers');
  }

  onLockerChanged() {
    this.orderLines = [];
  }

  onAddressChanged() {
    this.orderLines = [];
  }

  onLocationTypeChange() {
    this.selectedLockerId = null;
    this.selectedAddressId = null;
    this.initialStatus = null;
    this.orderLines = [];
  }

  onStep2Next() {
    if (!this.canGoToStep3) { return; }
    this.step = 3;
    this.loadEligibleServices();
  }

  loadEligibleServices() {
    this.loadingServices = true;
    const lockerId = this.locationType === 'locker' ? this.selectedLockerId : undefined;
    this.$store.dispatch('orderModule/fetchEligibleServices', lockerId);
    this.$store.dispatch('orderModule/fetchLockerCellSizes', { lockerId });
    this.$store.dispatch('orderModule/fetchEligibleServiceProviders', { lockerId });
  }

  onServiceSelected(item: any) {
    if (!item) { return; }
    this.orderLines.push({service: item.service, quantity: 1});
    this.$nextTick(() => { this.serviceToAdd = null; });
  }

  removeLine(index: number) {
    this.orderLines.splice(index, 1);
  }

  removeSelectedToAdd(index: number) {
    this.selectedServicesToAdd.splice(index, 1);
  }

  onAddSelectedServices() {
    this.selectedServicesToAdd.forEach((item: any) => {
      if (item && item.service) {
        this.orderLines.push({ service: item.service, quantity: 1 });
      }
    });
    this.selectedServicesToAdd = [];
  }

  onAddGenericArticle() {
    if (!this.genericForm.description || !this.genericForm.serviceProviderId) { return; }
    if (!this.genericForm.price || this.genericForm.price <= 0) {
      this.genericPriceError = String(this.$t('common.required'));
      return;
    }
    this.genericPriceError = '';
    const hasCellSizeOptions = Array.isArray(this.lockerCellSizes) && this.lockerCellSizes.length > 0;
    if (hasCellSizeOptions && this.genericForm.lockerCellSizeId === null) {
      this.genericCellSizeError = String(this.$t('orderCreate.step3.genericArticleMinCompartmentRequired'));
      return;
    }
    this.genericCellSizeError = '';
    const provider = this.eligibleProviders.find((p) => p.id === this.genericForm.serviceProviderId);
    this.genericArticleLines.push({
      description: this.genericForm.description,
      price: this.genericForm.price || 0,
      serviceProviderId: this.genericForm.serviceProviderId,
      providerName: provider ? provider.name : '',
      lockerCellSizeId: this.genericForm.lockerCellSizeId,
    });
    this.genericForm = { description: '', price: 0, serviceProviderId: null, lockerCellSizeId: null };
    this.genericCellSizeError = '';
    this.showGenericDialog = false;
  }

  removeGenericArticle(index: number) {
    this.genericArticleLines.splice(index, 1);
  }

  onSubmit() {
    if (!this.selectedUser || !this.canGoToStep3) { return; }
    if (this.orderLines.length === 0 && this.genericArticleLines.length === 0) { return; }
    if (this.locationType === 'address' && !this.initialStatus) { return; }

    const spIds = this.selectedServiceProviderIds;

    // serviceProviderId: from regular services, or from first generic article if no regular services
    let serviceProviderId: number | null = spIds.length > 0 ? spIds[0] : null;
    if (!serviceProviderId && this.genericArticleLines.length > 0) {
      serviceProviderId = this.genericArticleLines[0].serviceProviderId;
    }
    if (!serviceProviderId) { return; }

    const services = this.orderLines.map((l) => ({
      id: l.service.id,
      quantity: l.quantity,
    }));

    const genericItems = this.genericArticleLines.map((g) => ({
      name: g.description,
      price: g.price,
      service_provider_id: g.serviceProviderId,
      locker_cell_size_id: g.lockerCellSizeId,
      quantity: 1,
    }));

    this.creating = true;
    this.createError = '';

    this.$store.dispatch('orderModule/createManualOrder', {
      userId: this.selectedUser.id,
      lockerId: this.locationType === 'locker' ? this.selectedLockerId : null,
      addressId: this.locationType === 'address' ? this.selectedAddressId : null,
      initialStatus: this.locationType === 'address' ? this.initialStatus : null,
      serviceProviderId,
      services,
      genericItems,
      note: this.note || null,
      promoCode: this.promoCode || null,
    });
  }
}
